import { useCallback, type KeyboardEvent } from 'react';

/**
 * A compact integer input with its own stepper INSIDE the field.
 *
 * PrimeReact's InputNumber puts its buttons in a stacked group beside the input,
 * which on a dense form reads as two controls rather than one: the buttons are as
 * tall as the field, take a full colour block, and pull the eye away from the
 * value. Here the chevrons sit inside the right edge, sized to the text, so the
 * field stays one object and one grid cell.
 *
 * `null` is a first-class value, not a synonym for 0 — several callers use blank
 * to mean "inherit the default" while a typed 0 is a real setting (run retention:
 * 0 keeps history forever). Clearing the box yields null; it never silently
 * becomes a number.
 */
export interface NumberStepperProps {
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
  /** how much one press of a chevron (or one arrow key) moves the value */
  step?: number;
  /** shown when the value is null — e.g. "inherit" */
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  ariaLabel?: string;
}

const NumberStepper = ({
  value, onChange, min, max, step = 1, placeholder, disabled,
  className = '', id, ariaLabel,
}: NumberStepperProps) => {
  const clamp = useCallback((n: number) => {
    let out = n;
    if (typeof min === 'number' && out < min) out = min;
    if (typeof max === 'number' && out > max) out = max;
    return out;
  }, [min, max]);

  /* Stepping from empty starts at the floor rather than at 0: for a field whose
     min is 1, an empty box that jumps to 0 on the first click is invalid the
     instant it is touched. */
  const bump = (delta: number) => {
    if (disabled) return;
    const base = typeof value === 'number' ? value : (min ?? 0) - delta;
    onChange(clamp(base + delta));
  };

  const handleType = (raw: string) => {
    const t = raw.trim();
    if (t === '') { onChange(null); return; }
    // Digits only (with an optional leading -, when the range allows it). Anything
    // else is ignored rather than reverting the field, so a half-typed value is
    // not destroyed mid-keystroke.
    if (!/^-?\d*$/.test(t)) return;
    if (t === '-') return;
    const n = Number(t);
    if (Number.isNaN(n)) return;
    onChange(n);
  };

  // Clamping happens on blur, not per keystroke: clamping while typing makes a
  // value like 30 impossible to reach when the max is 9 — the 3 snaps first.
  const handleBlur = () => {
    if (typeof value !== 'number') return;
    const c = clamp(value);
    if (c !== value) onChange(c);
  };

  const handleKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') { e.preventDefault(); bump(step); }
    if (e.key === 'ArrowDown') { e.preventDefault(); bump(-step); }
  };

  const atMin = typeof value === 'number' && typeof min === 'number' && value <= min;
  const atMax = typeof value === 'number' && typeof max === 'number' && value >= max;

  const chevron = 'flex h-1/2 w-full items-center justify-center text-gray-400 transition-colors '
    + 'hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-30 dark:hover:text-indigo-400';

  return (
    <div className={`relative ${className}`}>
      <input
        id={id}
        aria-label={ariaLabel}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        disabled={disabled}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(e) => handleType(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKey}
        /* pr-6 leaves room for the stepper. Height and border match the app's
           other inputs so this sits level with a Dropdown in the same row. */
        className="h-[38px] w-full rounded-md border border-gray-300 bg-white py-1.5 pl-2.5 pr-6
                   text-sm text-gray-800 outline-none transition-colors
                   placeholder:text-gray-400
                   focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30
                   disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400
                   dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100
                   dark:disabled:bg-gray-900"
      />
      <div className="absolute right-px top-px bottom-px flex w-5 flex-col">
        <button type="button" tabIndex={-1} className={chevron}
          disabled={disabled || atMax} onClick={() => bump(step)} aria-label="Increase">
          <i className="pi pi-chevron-up" style={{ fontSize: '0.55rem' }} />
        </button>
        <button type="button" tabIndex={-1} className={chevron}
          disabled={disabled || atMin} onClick={() => bump(-step)} aria-label="Decrease">
          <i className="pi pi-chevron-down" style={{ fontSize: '0.55rem' }} />
        </button>
      </div>
    </div>
  );
};

export default NumberStepper;
